import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InboxIcon, Plane, Users, HelpCircle, GraduationCap, Laptop, MoreHorizontal } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { NoStaffNote } from "@/components/common/NoStaffNote";

const CATEGORY_ICON = {
  "Leave Request": Plane,
  "Meeting Request": Users,
  "Assistance Request": HelpCircle,
  "Training Request": GraduationCap,
  "Equipment Request": Laptop,
  "Other": MoreHorizontal,
};

const workingDaysBetween = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start), e = new Date(end);
  if (e < s) return 0;
  let n = 0;
  const d = new Date(s);
  while (d <= e) {
    if (d.getDay() !== 0 && d.getDay() !== 6) n++;
    d.setDate(d.getDate() + 1);
  }
  return n;
};

const defaults = (currentUser) => ({
  employee: currentUser?.name || "",
  employee_email: currentUser?.email || "",
  manager: "",
  category: "Leave Request",
  subject: "",
  notes: "",
  leave_type: "Annual",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date().toISOString().slice(0, 10),
  working_days: 1,
});

export default function EmployeeRequestModal({ open, onOpenChange, onCreated, currentUser, consultants = [], categories = [], leaveTypes = [] }) {
  const [form, setForm] = useState(defaults(currentUser));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(defaults(currentUser));
  }, [open, currentUser]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.category === "Leave Request") {
      set("working_days", workingDaysBetween(form.start_date, form.end_date));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.start_date, form.end_date, form.category]);

  const managers = useMemo(() => consultants.filter((c) => c.is_manager), [consultants]);
  const isLeave = form.category === "Leave Request";
  const Icon = CATEGORY_ICON[form.category] || InboxIcon;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee.trim()) return toast.error("Employee is required");
    if (!form.manager.trim()) return toast.error("Manager is required");
    if (isLeave) {
      if (!form.start_date || !form.end_date) return toast.error("Leave dates are required");
      if (new Date(form.end_date) < new Date(form.start_date)) return toast.error("End date must be after start date");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isLeave) {
        delete payload.leave_type; delete payload.start_date; delete payload.end_date; delete payload.working_days;
      }
      await endpoints.createEmployeeRequest(payload);
      toast.success("Request submitted");
      onCreated?.();
    } catch {
      toast.error("Could not submit request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-employee-request" className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <Icon className="h-4.5 w-4.5" />
            </div>
            <div>
              <DialogTitle>New employee request</DialogTitle>
              <DialogDescription>Submit a request to your manager.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3.5 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="er-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Send to (manager)</Label>
              <Select value={form.manager} onValueChange={(v) => set("manager", v)}>
                <SelectTrigger data-testid="er-manager-select"><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>
                  {managers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <NoStaffNote list={managers} testid="er-no-staff-note" />
            </div>
          </div>

          {isLeave ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Leave type</Label>
                  <Select value={form.leave_type} onValueChange={(v) => set("leave_type", v)}>
                    <SelectTrigger data-testid="er-leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {leaveTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Working days</span>
                  <span data-testid="er-working-days" className="font-mono font-bold text-lg">{form.working_days}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="er-start">Start date</Label>
                  <Input data-testid="er-start-date-input" id="er-start" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="er-end">End date</Label>
                  <Input data-testid="er-end-date-input" id="er-end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <div>
              <Label htmlFor="er-subject">Subject</Label>
              <Input data-testid="er-subject-input" id="er-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Short summary" />
            </div>
          )}

          <div>
            <Label htmlFor="er-notes">{isLeave ? "Reason / notes" : "Notes"}</Label>
            <Textarea data-testid="er-notes-input" id="er-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Add context or details..." />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="er-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Submitting..." : "Submit request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
